/**
 * Tiny presentational helpers shared across the SPA. Pure functions —
 * no React, no hooks, no DOM access.
 */

/**
 * Render an ISO 4217 amount in minor units (string-shaped to preserve
 * BigInt precision — see PR #126 review) as a human-readable major-
 * unit string with the currency code suffixed.
 *
 *   formatMinorUnits("12345", "KES")  → "123.45 KES"
 *   formatMinorUnits("-500", "KES")   → "-5.00 KES"
 *   formatMinorUnits("garbage", "KES") → "garbage KES"
 *
 * Two-decimal currencies are the only shape we serve today (KES, USD,
 * EUR, GBP). When a zero-decimal currency (JPY, KRW) lands on the
 * platform, this helper grows a per-currency dispatch via
 * Intl.NumberFormat — until then the explicit string-arithmetic keeps
 * the contract simple and BigInt-safe.
 */
export function formatMinorUnits(amount: string, currency: string): string {
  if (!/^-?\d+$/.test(amount)) return `${amount} ${currency}`;
  const negative = amount.startsWith("-");
  const digits = negative ? amount.slice(1) : amount;
  const padded = digits.padStart(3, "0");
  const major = padded.slice(0, -2);
  const minor = padded.slice(-2);
  return `${negative ? "-" : ""}${major}.${minor} ${currency}`;
}
